import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';

import { InstantEstimateChartComponent } from './instant-estimate-chart.component';
import { InstantEstimateService } from './instant-estimate.service';
import { clampIncome, roundToNearest100 } from './instant-estimate.math';
import {
  INSTANT_ESTIMATE_MAX_INCOME,
  INSTANT_ESTIMATE_MIN_INCOME,
  InstantEstimateConfig,
  InstantEstimateDisplayMode,
} from './instant-estimate.models';

@Component({
  selector: 'app-instant-estimate',
  standalone: true,
  imports: [
    CurrencyPipe,
    FormsModule,
    MatButtonModule,
    MatSliderModule,
    InstantEstimateChartComponent,
  ],
  templateUrl: './instant-estimate.component.html',
  styleUrl: './instant-estimate.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstantEstimateComponent implements OnDestroy {
  private readonly estimateService = inject(InstantEstimateService);
  private readonly router = inject(Router);

  readonly config = input.required<InstantEstimateConfig>();
  readonly displayMode = input<InstantEstimateDisplayMode>('full');
  readonly accentColor = input('#002999');
  readonly panelColor = input('#ffffff');
  readonly schoolId = input.required<string>();
  /** When false, hide the school-name eyebrow (e.g. branded header logo is already shown). */
  readonly showSchoolEyebrow = input(true);

  readonly minIncome = INSTANT_ESTIMATE_MIN_INCOME;
  readonly maxIncome = INSTANT_ESTIMATE_MAX_INCOME;

  readonly income = signal(120_000);
  /** Display string in the income field (may include commas while editing) */
  readonly incomeInputText = signal('120,000');
  readonly heroPulse = signal(false);

  readonly result = computed(() =>
    this.estimateService.estimate(this.config(), this.income())
  );

  readonly publishedCostDisplay = computed(() =>
    roundToNearest100(this.config().costOfAttendance)
  );

  readonly liveAnnouncement = computed(() => {
    const r = this.result();
    if (!r.isValid) {
      return 'Instant estimate data is unavailable for this school.';
    }
    return (
      `At ${this.formatCurrency(r.income)} family income, ` +
      `estimated net price ${this.formatCurrency(r.central)} per year. ` +
      `Likely range ${this.formatCurrency(r.rangeLow)} to ${this.formatCurrency(r.rangeHigh)}. ` +
      `Published cost ${this.formatCurrency(this.publishedCostDisplay())}.`
    );
  });

  private announceTimeout: ReturnType<typeof setTimeout> | null = null;
  private pulseTimeout: ReturnType<typeof setTimeout> | null = null;
  readonly announcedText = signal('');

  constructor() {
    effect(() => {
      const text = this.liveAnnouncement();
      if (this.announceTimeout) {
        clearTimeout(this.announceTimeout);
      }
      this.announceTimeout = setTimeout(() => {
        this.announcedText.set(text);
      }, 400);
    });
  }

  ngOnDestroy(): void {
    if (this.announceTimeout) {
      clearTimeout(this.announceTimeout);
    }
    if (this.pulseTimeout) {
      clearTimeout(this.pulseTimeout);
    }
  }

  onSliderChange(value: number): void {
    this.setIncome(value, true);
  }

  onIncomeInputChange(raw: string): void {
    this.incomeInputText.set(raw);
    const parsed = this.parseIncome(raw);
    if (
      parsed !== null &&
      parsed >= INSTANT_ESTIMATE_MIN_INCOME &&
      parsed <= INSTANT_ESTIMATE_MAX_INCOME
    ) {
      this.income.set(parsed);
    }
  }

  onIncomeInputBlur(): void {
    const parsed = this.parseIncome(this.incomeInputText());
    this.setIncome(parsed !== null ? parsed : this.income(), true);
  }

  startFullCalculator(): void {
    void this.router.navigate(['/app', this.schoolId()]);
  }

  readonly currencyDisplay = (value: number): string => this.formatCurrency(value);

  private setIncome(value: number, pulse = false): void {
    const clamped = clampIncome(value);
    this.income.set(clamped);
    this.incomeInputText.set(this.formatIncomeInput(clamped));
    if (pulse) {
      this.triggerHeroPulse();
    }
  }

  private triggerHeroPulse(): void {
    this.heroPulse.set(false);
    requestAnimationFrame(() => {
      this.heroPulse.set(true);
      if (this.pulseTimeout) {
        clearTimeout(this.pulseTimeout);
      }
      this.pulseTimeout = setTimeout(() => this.heroPulse.set(false), 450);
    });
  }

  private parseIncome(raw: string): number | null {
    const parsed = Number(String(raw).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private formatIncomeInput(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(
      value
    );
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  }
}
